import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatConsumption, formatDate, formatRelativeTime } from '@/utils/formatters';
import {
  User, Mail, Phone, Calendar, MoreVertical,
  Building2, Zap, Clock, AlertCircle, TrendingUp
} from 'lucide-react';

// Pipeline columns in logical sales order
const COLUMNS = [
  { id: 'neu', title: 'Neu', color: '#18DDEF', bgLight: '#e8fcfd', description: 'Erstkontakt' },
  { id: 'qualifiziert', title: 'Qualifiziert', color: '#007D88', bgLight: '#e6f2f3', description: 'Interesse bestaetigt' },
  { id: 'angebot_erstellt', title: 'Angebot erstellt', color: '#298DFF', bgLight: '#e6f3ff', description: 'In Bearbeitung' },
  { id: 'angebot_gesendet', title: 'Angebot gesendet', color: '#7429F8', bgLight: '#f0e6ff', description: 'Versendet' },
  { id: 'angebot_geoeffnet', title: 'Angebot geoeffnet', color: '#FF9100', bgLight: '#fff2e0', description: 'Kunde hat geoeffnet' },
  { id: 'in_verhandlung', title: 'In Verhandlung', color: '#FFC400', bgLight: '#fff8e0', description: 'Rueckfragen' },
  { id: 'angenommen', title: 'Angenommen', color: '#00D67F', bgLight: '#e6fbf2', description: 'Kunde hat zugesagt' },
  { id: 'vertrag', title: 'Vertrag', color: '#00a864', bgLight: '#d4f7e6', description: 'Abgeschlossen' },
  { id: 'verloren', title: 'Verloren', color: '#FF0000', bgLight: '#ffe6e6', description: 'Nicht erfolgreich' },
];

const priorityIndicator = (p) => {
  const map = {
    dringend: { color: '#FF0000', label: 'Dringend' },
    hoch: { color: '#FF9100', label: 'Hoch' },
    normal: { color: '#18DDEF', label: 'Normal' },
    niedrig: { color: '#9e9e9e', label: 'Niedrig' }
  };
  return map[p] || map.normal;
};

const LeadCard = ({ lead, onDragStart, onDragEnd, isDragging, onClick }) => {
  const prio = priorityIndicator(lead.priority);
  const initials = lead.company?.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?';
  const isOverdue = lead.next_action_date && lead.next_action_date < new Date().toISOString().slice(0, 10);

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, lead)}
      onDragEnd={onDragEnd}
      onClick={onClick}
      className={`bg-card border border-border rounded-lg p-3.5 cursor-grab active:cursor-grabbing hover:shadow-lg hover:border-primary/40 transition-all ${
        isDragging ? 'opacity-40 rotate-1 scale-95' : ''
      }`}
      style={{ borderLeft: `3px solid ${prio.color}` }}
    >
      {/* Top Row: Avatar + Name + Menu */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className="hk-avatar size-xs" style={{ background: '#007D88' }}>{initials}</div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-xs text-foreground truncate leading-tight">{lead.company?.name}</p>
            <p className="text-[10px] text-muted-foreground truncate">{lead.company?.plz} {lead.company?.city}</p>
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); }}
          className="p-1 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <MoreVertical size={14} />
        </button>
      </div>

      {/* Contact */}
      <div className="flex items-center gap-1.5 mb-2 text-xs text-muted-foreground">
        <User size={11} />
        <span className="truncate">{lead.contact?.first_name} {lead.contact?.last_name}</span>
      </div>

      {/* Consumption */}
      {lead.company?.annual_consumption_kwh && (
        <div className="flex items-center gap-1.5 mb-2 text-xs">
          <Zap size={11} className="text-primary" />
          <span className="font-semibold text-foreground">{formatConsumption(lead.company.annual_consumption_kwh)}</span>
        </div>
      )}

      {/* Next Action */}
      {lead.next_action && (
        <div className={`flex items-start gap-1.5 mt-2 p-2 rounded-md text-[11px] ${
          isOverdue ? 'bg-[#ffe6e6] text-[#cc0000]' : 'bg-muted text-muted-foreground'
        }`}>
          {isOverdue ? <AlertCircle size={11} className="flex-shrink-0 mt-0.5" /> : <Clock size={11} className="flex-shrink-0 mt-0.5" />}
          <div className="min-w-0">
            <p className="font-medium truncate">{lead.next_action}</p>
            {lead.next_action_date && <p className="text-[10px] opacity-80">{formatDate(lead.next_action_date)}</p>}
          </div>
        </div>
      )}

      {/* Footer: Priority + Created */}
      <div className="flex items-center justify-between mt-3 pt-2 border-t border-border/50">
        <div className="flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: prio.color }} />
          <span className="text-[10px] font-medium text-muted-foreground">{prio.label}</span>
        </div>
        <span className="text-[10px] text-muted-foreground">{formatRelativeTime(lead.created_at)}</span>
      </div>
    </div>
  );
};

const LeadKanban = ({ leads, onStatusChange }) => {
  const navigate = useNavigate();
  const [draggedLead, setDraggedLead] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
    // Set a transparent drag image for cleaner drag feel
    try {
      const dragImage = e.currentTarget.cloneNode(true);
      dragImage.style.opacity = '0.8';
      dragImage.style.transform = 'rotate(2deg)';
      e.dataTransfer.setData('text/plain', lead.id);
    } catch {}
  };

  const handleDragEnd = () => {
    setDraggedLead(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverColumn !== columnId) setDragOverColumn(columnId);
  };

  const handleDragLeave = (e) => {
    // Only clear if leaving the column entirely, not a child element
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverColumn(null);
    }
  };

  const handleDrop = (e, columnId) => {
    e.preventDefault();
    if (draggedLead && draggedLead.status !== columnId) {
      onStatusChange(draggedLead.id, columnId);
    }
    setDraggedLead(null);
    setDragOverColumn(null);
  };

  const leadsByStatus = COLUMNS.reduce((acc, col) => {
    acc[col.id] = leads.filter(l => l.status === col.id);
    return acc;
  }, {});

  return (
    <div className="hk-card overflow-hidden">
      <div className="hk-card-header">
        <h6>Sales Pipeline</h6>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <TrendingUp size={14} />
          <span>{leads.length} Leads in Pipeline</span>
        </div>
      </div>
      <div className="overflow-x-auto scrollbar-thin bg-muted/30">
        <div className="flex gap-4 p-4 min-w-max">
          {COLUMNS.map(col => {
            const colLeads = leadsByStatus[col.id];
            const isOver = dragOverColumn === col.id;
            return (
              <div
                key={col.id}
                className={`flex flex-col w-[280px] flex-shrink-0 rounded-lg transition-all ${
                  isOver ? 'ring-2 ring-primary ring-offset-2' : ''
                }`}
                onDragOver={(e) => handleDragOver(e, col.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, col.id)}
              >
                {/* Column Header */}
                <div
                  className="rounded-t-lg px-3 py-3 border-b-[3px]"
                  style={{ background: col.bgLight, borderBottomColor: col.color }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ background: col.color }} />
                      <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: col.color }}>
                        {col.title}
                      </h4>
                    </div>
                    <span
                      className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                      style={{ background: col.color, color: '#fff' }}
                    >
                      {colLeads.length}
                    </span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-0.5 ml-4">{col.description}</p>
                </div>

                {/* Column Body */}
                <div
                  className={`flex-1 min-h-[200px] p-2 space-y-2 rounded-b-lg transition-colors ${
                    isOver ? 'bg-primary/5' : 'bg-card/50'
                  }`}
                >
                  {colLeads.length === 0 ? (
                    <div className={`flex items-center justify-center h-full min-h-[150px] border-2 border-dashed rounded-lg transition-colors ${
                      isOver ? 'border-primary bg-primary/10' : 'border-border'
                    }`}>
                      <p className="text-xs text-muted-foreground text-center px-3">
                        {isOver ? 'Hier ablegen' : 'Keine Leads'}
                      </p>
                    </div>
                  ) : (
                    colLeads.map(lead => (
                      <LeadCard
                        key={lead.id}
                        lead={lead}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        isDragging={draggedLead?.id === lead.id}
                        onClick={() => navigate(`/app/leads/${lead.id}`)}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LeadKanban;
